import type { FC, ReactNode } from 'react'

import { ArrowRight, KeyRound } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button'
import { Foliage } from '@/components/landing/foliage'
import { ProductPanel } from '@/components/landing/product-panel'

import { DEMO_HREF } from '@/constants/navigation'

interface RisingWordsProps {
    words: Array<string>
    startIndex: number
}

const WORD_DELAY_MS = 110
const FIRST_WORD_MS = 150

/* Each headline word rises out of a soft blur, one after another */
const RisingWords: FC<RisingWordsProps> = ({ words, startIndex }): ReactNode => {
    return words.map((word, index) => (
        <span key={word}>
            <span
                className='inline-block animate-in duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] fill-mode-both fade-in blur-in-8 slide-in-from-bottom-8'
                style={{ animationDelay: `${FIRST_WORD_MS + (startIndex + index) * WORD_DELAY_MS}ms` }}
            >
                {word}
            </span>

            {index < words.length - 1 && ' '}
        </span>
    ))
}

export const Hero: FC = (): ReactNode => {
    return (
        <div className='relative'>
            <Foliage
                side='left'
                preload
                parallax={0.1}
                className='top-[10.5rem] -left-[calc(min(34vw,520px)*0.05)] w-[min(34vw,520px)]'
            />

            <Foliage
                side='right'
                preload
                parallax={0.16}
                className='top-[24rem] right-0 w-[min(29vw,440px)]'
            />

            <section id='hero' aria-labelledby='hero-title' className='relative z-20 px-5 pt-14 pb-12 text-center sm:pt-20 sm:pb-16'>
                <div className='sheen relative inline-flex animate-in items-center gap-2 overflow-hidden rounded-md border border-[#dfdbce] bg-[#ebe8de]/80 px-3 py-1.5 text-[13px] text-foreground/75 duration-700 fade-in slide-in-from-bottom-2'>
                    <span>
                        Made for:
                    </span>

                    <span className='inline-flex items-center gap-1.5'>
                        <span className='flex size-[18px] items-center justify-center rounded-[4px] bg-primary text-primary-foreground'>
                            <KeyRound className='size-3' />
                        </span>

                        <span className='font-medium text-foreground/85'>
                            Agents & brokerages
                        </span>
                    </span>
                </div>

                <h1
                    id='hero-title'
                    className='mt-8 flex items-center justify-center font-heading text-[clamp(3.6rem,8.6vw,6.6rem)] leading-[0.92] tracking-[-0.035em] text-foreground'
                >
                    <span aria-hidden='true' className='hidden animate-in text-[1.08em] text-foreground/85 delay-500 duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] fill-mode-both fade-in slide-in-from-right-40 sm:block'>
                        (
                    </span>

                    <span className='sm:px-[clamp(1.5rem,5.5vw,6rem)]'>
                        <span className='block sm:inline'>
                            <RisingWords words={['From', 'offer']} startIndex={0} />
                            {' '}
                        </span>

                        <em className='block sm:inline'>
                            <RisingWords words={['to', 'keys']} startIndex={2} />
                        </em>
                    </span>

                    <span aria-hidden='true' className='hidden animate-in text-[1.08em] text-foreground/85 delay-500 duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] fill-mode-both fade-in slide-in-from-left-40 sm:block'>
                        )
                    </span>
                </h1>

                <p className='mx-auto mt-7 max-w-[35rem] animate-in text-[17px] leading-[1.5] text-muted-foreground delay-700 duration-1000 fill-mode-both fade-in slide-in-from-bottom-3 sm:text-[19px]'>
                    An AI transaction coordinator for the stretch between accepted offer and closing day. Closewell tracks every deadline, chases every signature and keeps every party in the loop.
                </p>

                <div className='mt-9 animate-in delay-1000 duration-1000 fill-mode-both fade-in slide-in-from-bottom-3'>
                    <ButtonLink href={DEMO_HREF} size='lg'>
                        Meet your coordinator
                        <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                    </ButtonLink>
                </div>
            </section>

            <ProductPanel />
        </div>
    )
}
